<template>
    <div class="branches-container">
        <h2 class="section-title">Отделения ПЭК в Беларуси</h2>
        <div class="controls-and-map-wrapper">
            <div class="controls-and-cards">
                <select-input
                    v-if="cities.length > 0"
                    v-model="selectedCity"
                    :options="cities"
                    label="Отделение"
                    class="city-selector"
                />

                <div v-if="loading" class="loading-message">Загрузка отделений...</div>
                <div v-if="error" class="error-message">{{ error }}</div>

                <div v-if="selectedBranch" class="branch-card">
                    <h3 class="branch-card-city">{{ selectedBranch.city }}</h3>
                    <div class="branch-card-text">
                        <p class="branch-card-value"><span class="branch-card-label">Адрес: </span>{{ selectedBranch.address }}</p>
                        <p class="branch-card-value"><span class="branch-card-label">Телефон: </span>{{ formattedPhoneNumber }}</p>
                    </div>
                </div>
            </div>
            <div class="map-wrapper">
                <div id="map-container" style="width: 100%; height: 100%;"></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { getBelarusBranches } from '~/api/getBelarusBranches';
import SelectInput from '~/components/Input/SelectInput.vue';

const config = useRuntimeConfig();
const apiKey = config.public.API_MAPS_YANDEX_KEY;

const cities = ref([]);
const selectedCity = ref(null);
const mapIsReady = ref(false);
const loading = ref(true);
const error = ref(null);
let map = null;

const branches = ref([]);

const selectedBranch = computed(() => {
    return branches.value.find(branch => branch.city === selectedCity.value?.value);
});

function formatPhoneNumber(phoneNumber) {
    if (!phoneNumber) return '';
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{2})(\d{3})(\d{2})(\d{2})$/);
    if (match) {
        return `+${match[1]}(${match[2]})${match[3]}-${match[4]}-${match[5]}`;
    }
    return phoneNumber;
}

const formattedPhoneNumber = computed(() => {
    return formatPhoneNumber(selectedBranch.value?.phone);
});

async function fetchBranches() {
    loading.value = true;
    error.value = null;
    try {
        const response = await getBelarusBranches();

        if (response.status !== 200) {
            throw new Error(`Server error! status: ${response.status}`);
        }

        const belarusBranches = response.data;

        if (!Array.isArray(belarusBranches)) {
            throw new Error('Invalid data format from server');
        }

        branches.value = belarusBranches;

        const uniqueCities = [...new Set(belarusBranches.map(b => b.city).filter(Boolean))];
        cities.value = uniqueCities.map((city, index) => ({ id: index, value: city }));
        if (cities.value.length > 0) {
            const minskCity = cities.value.find(city => city.value === 'Минск');
            selectedCity.value = minskCity || cities.value[0];
        }

    } catch (e) {
        console.error('Failed to fetch branches from server:', e);
        error.value = 'Не удалось загрузить данные об отделениях с сервера.';
    } finally {
        loading.value = false;
    }
}


function loadYandexMap() {
    const script = document.createElement('script');
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${apiKey}&lang=ru_RU`;
    script.onload = () => {
        window.ymaps.ready(initMap);
    };
    script.onerror = () => {
        error.value = 'Не удалось загрузить скрипт Яндекс.Карт.';
    }
    document.head.appendChild(script);
}

function initMap() {
    if (!selectedBranch.value || !selectedBranch.value.coordinates) {
        error.value = 'Нет данных о координатах для инициализации карты.';
        return;
    }
    mapIsReady.value = true;
    map = new window.ymaps.Map('map-container', {
        center: selectedBranch.value.coordinates,
        zoom: 10,
        controls: ['zoomControl', 'fullscreenControl'],
    });

    branches.value.forEach(branch => {
        if (branch.coordinates) {
            const formattedBalloonPhone = formatPhoneNumber(branch.phone);

            const placemark = new window.ymaps.Placemark(branch.coordinates, {
                balloonContentHeader: branch.city,
                balloonContentBody: branch.address,
                balloonContentFooter: formattedBalloonPhone
            }, {
                iconLayout: 'default#image',
                iconImageHref: '/images/pin.png',
                iconImageSize: [42, 54],
            });
            map.geoObjects.add(placemark);
        }
    });
}

watch(selectedCity, (newCity) => {
    if (!newCity) return;
    const branch = branches.value.find(b => b.city === newCity.value);
    if (map && branch && branch.coordinates) {
        map.setCenter(branch.coordinates, 12);
    }
});

onMounted(async () => {
    await fetchBranches();
    if (branches.value.length > 0) {
        loadYandexMap();
    }
});

</script>

<style scoped>
.branches-container {
    margin-top: 80px;
    display: flex;
    flex-direction: column;
    gap: 40px;
}

.controls-and-map-wrapper {
    padding: 32px;
    display: flex;;
    gap: 24px;
    background-color: #F5F5F5;
    border-radius: 16px;
}

.controls-and-cards {
    display: flex;
    flex-direction: column;
    gap: 24px;
}


.branch-card {
    padding: 24px;
    border-radius: 12px;
    background-color: #FFFFFF;
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: 354px;
}

.branch-card-city {
    font-weight: 500;
    font-style: Medium;
    font-size: 20px;
    leading-trim: NONE;
    line-height: 28px;
    letter-spacing: 0%;
    margin: 0;
}

.branch-card-text {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.branch-card-value {
    font-weight: 400;
    font-style: Regular;
    font-size: 18px;
    leading-trim: NONE;
    line-height: 26px;
    letter-spacing: 0%;
    vertical-align: middle;
    margin: 0;
}

.branch-card-label {
    color: #AAAAAA;
}

.map-wrapper {
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 0;
    border-radius: 16px;
    max-width: 838px;
    min-height: 500px
}

.loading-message, .error-message {
    padding: 15px;
}


.section-title {
    font-weight: 600;
    font-style: SemiBold;
    font-size: 36px;
    leading-trim: NONE;
    line-height: 46px;
    letter-spacing: 0%;
    margin: 0;
}

@media screen and (max-width: 1279px) {
    .section-title {
        font-size: 24px;
        line-height: 32px;
    }
    .controls-and-map-wrapper {
        flex-direction: column;
        gap: 20px;
    }

    .map-wrapper {
        max-width: 100%;
        min-height: 300px;
    }

    .branch-card {
        max-width: 100%;
    }
}
@media screen and (max-width: 768px) {
    .section-title {
        font-size: 22px;
        line-height: 28px;
    }
    .map-wrapper {
        min-height: 200px;
    }
}
</style>
